/**
 * Progressive Web App helpers.
 *
 * The install-hint decision logic is kept as pure functions so it can be unit
 * tested without a DOM.
 */

/** localStorage key holding the epoch ms at which the user dismissed the hint. */
export const INSTALL_HINT_DISMISSED_KEY = "pwaInstallHintDismissedAt";

/** How long a dismissal suppresses the hint. */
export const INSTALL_HINT_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * iOS never fires `beforeinstallprompt`, so an iOS device is detected from the
 * user agent. iPadOS 13+ reports a desktop ("Macintosh") user agent, so touch
 * point count is used to disambiguate it from a real Mac.
 */
export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
    if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
    return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

/**
 * Only Safari on iOS exposes the "Add to Home Screen" share action that the
 * hint describes, so third party iOS browsers are excluded to avoid giving
 * instructions the user cannot follow.
 */
export function isIosSafari(userAgent: string, maxTouchPoints: number): boolean {
    if (!isIosDevice(userAgent, maxTouchPoints)) return false;
    // Chrome, Firefox, Edge and Opera on iOS.
    if (/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(userAgent)) return false;
    return /Safari/i.test(userAgent);
}

/** True when a dismissal is still within the snooze window. */
export function isHintSnoozed(dismissedAt: number | null, now: number): boolean {
    if (dismissedAt === null || !Number.isFinite(dismissedAt)) return false;
    // A timestamp in the future indicates a tampered or clock-skewed value; treat
    // it as an active dismissal rather than showing the hint on every load.
    if (dismissedAt > now) return true;
    return now - dismissedAt < INSTALL_HINT_SNOOZE_MS;
}

export interface InstallHintInput {
    userAgent: string;
    maxTouchPoints: number;
    isStandalone: boolean;
    dismissedAt: number | null;
    now: number;
}

/** Decides whether the iOS "Add to Home Screen" hint should be rendered. */
export function shouldShowIosInstallHint(input: InstallHintInput): boolean {
    if (input.isStandalone) return false;
    if (!isIosSafari(input.userAgent, input.maxTouchPoints)) return false;
    return !isHintSnoozed(input.dismissedAt, input.now);
}

/** Parses a persisted dismissal timestamp, tolerating absent or corrupt values. */
export function parseDismissedAt(raw: string | null): number | null {
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

/** True when the page is running as an installed app rather than a browser tab. */
export function isRunningStandalone(): boolean {
    if (typeof window === "undefined") return false;
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    // Safari on iOS does not support the display-mode media query and uses this
    // non-standard flag instead.
    return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}
